import {
    createContext,
    useState,
    useEffect,
    useCallback,
} from "react";
import { Col, Row, Container } from "react-bootstrap";
import Loader2 from "../../../../../components/Loader2.jsx";
import { SnackbarAlert } from "../../../../../components/SnackbarAlert.js";
import api from "../../../../../service/api.js";
import List from "./list.js";
import Search from "./Search.js";

export const ContextProvider = createContext(null);

export default function WriteOffApproval() {
    const context = null;
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [perPage, setPerPage] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [lastPage, setLastPage] = useState(1);
    const [createdByList, setCreatedByList] = useState([]);
    const [approvedByList, setApprovedByList] = useState([]);

    const [searchValue, setSearchValue] = useState({
        invoice_number: "",
        requestor_name: "",
        approver_name: "",
        status: "",
        approved_start_date: null,
        approved_end_date: null,
    });

    const [notif, setNotif] = useState({
        notifMsg: "",
        open: false,
        severity: "success",
    });

    const search = useCallback((page = 1) => {
        getWriteOffs(searchValue, page);
    }, [searchValue]);

    const getWriteOffs = useCallback(
        async (searchParams, page = 1) => {
            try {
                setLoading(true);
                const response = await api.get(
                        "tms/invoice/write-off",
                        { ...searchParams, page }
                    );

                setData(response.data.write_offs);                 // current page
                setPerPage(response.per_page);          // per page
                setCurrentPage(response.current_page);  // current page
                setLastPage(response.last_page);        // total pages
                
                // Extract request_by and approved_by lists from response
                if (response.data.created_by) {
                    const createdByOptions = response.data.created_by;
                    setCreatedByList(createdByOptions);
                }
                
                if (response.data.approved_by) {
                    const approvedByOptions = response.data.approved_by;
                    setApprovedByList(approvedByOptions);
                }
            } catch (error) {
                console.error("Error fetching write-offs:", error);
                setNotif({
                    notifMsg: error.message || "An error occurred during fetching of data.",
                    open: true,
                    severity: "danger",
                });
            } finally {
                setLoading(false);
            }
        },
        []
    );

    const handleApprove = useCallback(async (writeOff) => {
        try {
            setLoading(true);
            await api.post(`tms/invoice/write-off/update-status/${writeOff.id}`, { status: "APPROVED" });
            setNotif({
                notifMsg: "Write-off approved successfully",
                open: true,
                severity: "success",
            });
            search();
        } catch (error) {
            setNotif({
                notifMsg: error.message || "Failed to approve write-off",
                open: true,
                severity: "danger",
            });
        } finally {
            setLoading(false);
        }
    }, [search]);

    const handleReject = useCallback(async (writeOff) => {
        try {
            setLoading(true);
            await api.post(`tms/invoice/write-off/update-status/${writeOff.id}`, { status: "REJECTED" });
            setNotif({
                notifMsg: "Write-off rejected successfully",
                open: true,
                severity: "success",
            });
            search();
        } catch (errors) {
            setNotif({
                notifMsg: errors.message || "Failed to reject write-off",
                open: true,
                severity: "danger",
            });
        } finally {
            setLoading(false);
        }
    }, [search]);


    const initialLoad = useCallback(() => {
        getWriteOffs({}, 1);
    }, []);

    useEffect(() => {
        initialLoad();
    }, [initialLoad]);

    return (
        <ContextProvider.Provider value={context}>
            <Container fluid>
                <Row>
                    <Col md={12}>
                        <h3 className="fw-bolder m-0">Write Off Approval</h3>
                    </Col>
                </Row>

                <Row>
                    <Col xxl={12}>
                        <Search
                            setSearchValue={setSearchValue}
                            searchValue={searchValue}
                            search={search}
                            createdByList={createdByList}
                            approvedByList={approvedByList}
                        />
                    </Col>
                </Row>

                <Row>
                    <Col xxl={12}>
                        {loading ? (
                            <Loader2 />
                        ) : (
                            <List
                                writeOffs={data ?? []}
                                currentPage={currentPage}
                                perPage={perPage}
                                lastPage={lastPage}
                                onPageChange={(page) => search(page)}
                                onApprove={handleApprove}
                                onReject={handleReject}
                            />
                        )}
                    </Col>
                </Row>

            </Container>
            <SnackbarAlert notif={notif} setNotif={setNotif} />
            
        </ContextProvider.Provider>
    );
}